import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { isDespiaNative } from "@/lib/isDespiaNative";
import companyLogo from "@/assets/ulture-logo-transparent.png";

const APP_STORE_URL = "https://apps.apple.com/us/app/ulture-pest/id6761015705";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.despia.ulturepest";
const DISMISS_KEY = "ulture_app_banner_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type Platform = "ios" | "android" | "other";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  // iPadOS 13+ reports as Macintosh with touch
  if (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

/**
 * Returns true on iOS Safari only (NOT Chrome/Firefox/Edge/etc on iOS).
 * In iOS Safari we suppress our custom banner because Apple's native
 * Smart App Banner (declared via <meta name="apple-itunes-app">) shows
 * automatically. In every other iOS browser, that meta tag is ignored,
 * so we show our own banner instead.
 */
function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
  if (!isIos) return false;
  // CriOS=Chrome iOS, FxiOS=Firefox, EdgiOS=Edge, OPiOS=Opera, GSA=Google Search App
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua);
}

function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 1024;
}

/**
 * Slick "download our app" banner shown on public pages.
 *
 * Hidden when:
 *  - User is in the Despia native app
 *  - Viewport is desktop-sized (lg+)
 *  - User dismissed it within the last 7 days
 *  - User is on iOS Safari (Apple's native Smart App Banner takes over)
 */
export function AppDownloadBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<Platform>("other");

  useEffect(() => {
    if (isDespiaNative()) return;
    if (!isMobileViewport()) return;
    if (isIosSafari()) return; // Apple's native banner handles this case

    try {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || "0");
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;
    } catch {
      /* noop */
    }

    setPlatform(detectPlatform());
    const id = window.setTimeout(() => setVisible(true), 250);
    return () => window.clearTimeout(id);
  }, []);

  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Reserve EXACTLY the banner's rendered height for the page below it. Measured from
  // the element (not the old hardcoded 76px + safe-area guess) because the safe-area
  // env() the banner actually gets differs per device/browser chrome — any mismatch
  // left the floating header hovering below an empty strip. ResizeObserver keeps it
  // exact across rotation and dynamic-toolbar changes; no banner → no offset, so the
  // header hugs the very top.
  useEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!visible || !el) {
      root.style.removeProperty("--public-app-banner-offset");
      return;
    }
    const apply = () =>
      root.style.setProperty("--public-app-banner-offset", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--public-app-banner-offset");
    };
  }, [visible]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    setVisible(false);
  };

  const handleOpen = () => {
    const url = platform === "android" ? PLAY_STORE_URL : APP_STORE_URL;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 inset-x-0 z-[80] lg:hidden bg-background shadow-[0_2px_14px_hsl(var(--foreground)/0.14)]"
      role="region"
      aria-label={t("landing.appBanner.aria")}
      style={{ paddingTop: "var(--safe-area-top, env(safe-area-inset-top, 0px))" }}
    >
      <div className="h-[76px] px-4 flex items-center">
        <div className="flex w-full items-center gap-3">
          <button
            type="button"
            onClick={handleDismiss}
            className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center shrink-0"
            aria-label={t("landing.appBanner.dismiss")}
          >
            <X className="w-4 h-4" />
          </button>
          <div className="h-11 w-11 rounded-xl bg-muted flex items-center justify-center shrink-0 overflow-hidden">
            <img
              src={companyLogo}
              alt="Ulture Pest"
              className="h-10 w-10 object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-tight text-foreground truncate">
              {t("landing.appBanner.title")}
            </p>
            <p className="text-xs leading-tight text-muted-foreground truncate">
              {t("landing.appBanner.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleOpen}
            className="shrink-0 h-10 px-5 rounded-full bg-primary text-primary-foreground text-xs font-bold tracking-wide uppercase shadow-sm hover:opacity-90 transition-opacity"
          >
            {t("landing.appBanner.ctaOpen")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AppDownloadBanner;
